import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AppDateInput from "../inputs/AppDateInput";
import { AppColors } from "../../constants/AppColors";

type CalendarFiltersCardProps = {
  t: (key: string, values?: Record<string, string | number>) => string;
  startDate: string;
  endDate: string;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  applyFilters: () => void;
  setThisWeek: () => void;
  setNext7Days: () => void;
  setNext14Days: () => void;
  setThisMonth: () => void;
  goToToday?: () => void;
  setFullYear?: () => void;
};

const CalendarFiltersCard = ({
  t,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  applyFilters,
  setThisWeek,
  setNext7Days,
  setNext14Days,
  setThisMonth,
  goToToday,
  setFullYear,
}: CalendarFiltersCardProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.5, md: 2 },
          "&:last-child": {
            pb: { xs: 1.5, md: 2 },
          },
        }}
      >
        <Typography sx={{ fontWeight: 900, mb: 1 }}>
          {t("calendar.filters")}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <AppDateInput
            label={t("calendar.startDate")}
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />

          <AppDateInput
            label={t("calendar.endDate")}
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />

          <Button
            variant="contained"
            startIcon={<FilterAltOutlinedIcon />}
            onClick={applyFilters}
            sx={{
              borderRadius: "999px",
              fontWeight: 900,
              minHeight: { xs: 42, md: 36 },
            }}
          >
            {t("common.apply")}
          </Button>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 2,
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          {goToToday ? (
            <Button
              size="small"
              variant="outlined"
              onClick={goToToday}
              sx={{ borderRadius: "999px", fontWeight: 900 }}
            >
              {t("calendar.today")}
            </Button>
          ) : null}

          <Button
            size="small"
            variant="outlined"
            onClick={setThisWeek}
            sx={{ borderRadius: "999px", fontWeight: 900 }}
          >
            {t("calendar.thisWeek")}
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={setNext7Days}
            sx={{ borderRadius: "999px", fontWeight: 900 }}
          >
            {t("calendar.next7Days")}
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={setNext14Days}
            sx={{ borderRadius: "999px", fontWeight: 900 }}
          >
            {t("calendar.next14Days")}
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={setThisMonth}
            sx={{ borderRadius: "999px", fontWeight: 900 }}
          >
            {t("calendar.thisMonth")}
          </Button>

          {setFullYear ? (
            <Button
              size="small"
              variant="outlined"
              onClick={setFullYear}
              sx={{ borderRadius: "999px", fontWeight: 900 }}
            >
              {t("calendar.fullYear")}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CalendarFiltersCard;
